import qs from "qs";
import { toast } from "sonner";

import i18n from "@/configs/i18n";
import { env } from "@/configs/env";

import { ERROR_TOAST_DEDUPE_MS, REQUEST_TIMEOUT } from "./constants";
import { ApiError } from "./error";
import type { ApiErrorBody, RequestOptions } from "./types";

export * from "./error";
export type {
  ApiErrorBody,
  Paginated,
  PaginationMeta,
  PaginationQuery,
  RequestOptions,
} from "./types";

/**
 * Bir xil xabar bir necha vidjetdan birdan kelishi mumkin (dashboard'da
 * to'rtta so'rov birga yiqiladi). Foydalanuvchi bitta jumlani to'rt marta
 * o'qishdan hech narsa yutmaydi.
 */
const shownErrors = new Set<string>();

const showErrorToast = (message: string) => {
  if (shownErrors.has(message)) return;
  shownErrors.add(message);
  toast.error(message);
  setTimeout(() => shownErrors.delete(message), ERROR_TOAST_DEDUPE_MS);
};

const shouldToast = (status: number, skip: RequestOptions["skipErrorToast"]) => {
  // 401 — sessiya oqimining bir qismi, uni `AuthGuard` hal qiladi.
  if (status === 401) return false;
  if (Array.isArray(skip)) return !skip.includes(status);
  return !skip;
};

/**
 * Xato kodini foydalanuvchi o'qiydigan matnga aylantiradi (ADR 0002).
 * `details` to'g'ridan-to'g'ri i18n parametrlariga uzatiladi.
 */
export const getErrorMessage = (error: unknown): string => {
  if (!(error instanceof ApiError)) return i18n.t("errors.UNKNOWN");

  const key = `errors.${error.code}`;
  if (!i18n.exists(key)) return i18n.t("errors.UNKNOWN");

  const details =
    error.details && typeof error.details === "object"
      ? (error.details as Record<string, unknown>)
      : {};

  // i18next batafsil natija turini qaytarishi mumkin; bu yerda
  // interpolatsiya bilan har doim oddiy matn keladi.
  return i18n.t(key, details as never) as unknown as string;
};

const buildUrl = (path: string, query?: RequestOptions["query"]) => {
  const base = env.VITE_API_BASE_URL.replace(/\/+$/, "");
  const search = query
    ? qs.stringify(query, { skipNulls: true, addQueryPrefix: true, indices: false })
    : "";
  return `${base}/${path.replace(/^\/+/, "")}${search}`;
};

/**
 * Yagona API chaqiruvchi.
 *
 * Sessiya `httpOnly` cookie'da (ADR 0004) va u `.qahvadosh.uz` domeniga
 * yozilgani uchun admin panel web bilan bir xil sessiyani ko'radi.
 * Token JS ga umuman ko'rinmaydi — shuning uchun bu yerda hech qanday
 * `Authorization` sarlavhasi yo'q, faqat `credentials: "include"`.
 */
/**
 * Sessiyani yangilash — bir vaqtda BITTA so'rov.
 *
 * Refresh token har ishlatilganda almashtiriladi va eskisi bekor
 * qilinadi (backend `auth.service`). Beshta so'rov barobar 401 olib,
 * beshtasi ham refresh qilsa, birinchisi tokenni almashtiradi va
 * qolgan to'rttasi "bekor qilingan token" bilan keladi — backend buni
 * o'g'irlik belgisi deb bilib, foydalanuvchining BARCHA sessiyalarini
 * yopadi. Ya'ni himoya mexanizmi oddiy sahifa yuklanishida ishga
 * tushib ketardi.
 */
let refreshInFlight: Promise<boolean> | null = null;

/**
 * Sessiya butunlay tugaganda chaqiriladi (refresh ham rad etildi).
 *
 * Callback orqali, chunki bu modul React'ni ham, `queryClient` ni ham
 * bilmasligi kerak: `configs/react-query` shu moduldan import qiladi,
 * teskarisi aylanma bog'liqlik bo'lardi. Ro'yxatdan o'tkazishni
 * `AuthGuard` bajaradi — sessiya holati aynan unda yashaydi.
 */
let sessionLostHandler: (() => void) | null = null;

export const onSessionLost = (handler: (() => void) | null): void => {
  sessionLostHandler = handler;
};

const refreshSession = (): Promise<boolean> => {
  refreshInFlight ??= fetch(`${env.VITE_API_BASE_URL.replace(/\/+$/, "")}/auth/refresh`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json" },
  })
    .then(response => response.ok)
    .catch(() => false)
    .finally(() => {
      refreshInFlight = null;
    });

  return refreshInFlight;
};

export async function request<T>(
  path: string,
  options: RequestOptions = {},
  isRetry = false,
): Promise<T> {
  const { method = "GET", body, query, skipErrorToast, headers, signal, ...rest } = options;

  const isFormData = body instanceof FormData;
  const requestHeaders = new Headers(headers);
  if (!isFormData && body !== undefined) requestHeaders.set("Content-Type", "application/json");
  requestHeaders.set("Accept", "application/json");
  requestHeaders.set("Accept-Language", i18n.language || "uz");

  // Chaqiruvchi o'z `signal` ini bergan bo'lsa (React Query bekor qilishi)
  // ikkalasi ham ishlashi kerak.
  const timeout = AbortSignal.timeout(REQUEST_TIMEOUT);
  const combined = signal ? AbortSignal.any([signal, timeout]) : timeout;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      ...rest,
      method,
      headers: requestHeaders,
      credentials: "include",
      signal: combined,
      body: isFormData ? body : body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (error) {
    // Bekor qilish xato emas: komponent unmount bo'ldi yoki so'rov
    // yangisi bilan almashtirildi. Toast ko'rsatish chalg'itadi.
    if (signal?.aborted) throw error;

    const networkError = new ApiError(0, {
      error: { code: "NETWORK_ERROR", message: String(error) },
      requestId: "",
      path,
      timestamp: new Date().toISOString(),
    });
    if (shouldToast(0, skipErrorToast)) showErrorToast(getErrorMessage(networkError));
    throw networkError;
  }

  /**
   * Access token muddati tugadi (15 daqiqa) — refresh cookie'si 30 kun
   * yashaydi, ya'ni sessiya hali tirik.
   *
   * Bu yo'l ILGARI UMUMAN YO'Q edi: panel `refresh` ni bilmasdi va
   * ertalab ochiq qolgan tab jimgina o'lardi — barcha so'rovlar 401
   * bilan qaytardi, toast ko'rsatilmasdi (401 ataylab jim), jadvallar
   * esa "yuklanmadi" deb turardi. Sabab faqat F5 dan keyin ko'rinardi.
   */
  // `/auth/*` BUTUNLAY chiqarib tashlangan edi, va aynan shu sabab
  // yo'l ishlamasdi: panel ochilganda birinchi va yagona so'rov —
  // `/auth/me`. Access cookie 15 daqiqa yashaydi, refresh esa 30
  // kun; ya'ni 15 daqiqadan keyin panelni ochgan admin TIRIK sessiya
  // bilan "kirish kerak" ekranini ko'rardi.
  //
  // Faqat `refresh` ning o'zi chiqariladi — u xom `fetch` ishlatadi,
  // demak rekursiya yo'q, va `isRetry` baribir bitta urinish bilan
  // cheklaydi.
  if (response.status === 401 && !isRetry && path !== "/auth/refresh") {
    if (await refreshSession()) return request<T>(path, options, true);

    // Yangilanmadi — sessiya haqiqatan tugagan. Ilgari panel bu holatda
    // ham eski `me` keshi bilan ishlab turardi: jadvallar bo'sh, sabab
    // yo'q, kirish ekrani esa faqat F5 dan keyin chiqardi.
    sessionLostHandler?.();
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown;
  try {
    payload = text ? JSON.parse(text) : undefined;
  } catch {
    // Gateway HTML xato sahifasi qaytardi.
    payload = undefined;
  }

  if (!response.ok) {
    const apiError = new ApiError(response.status, payload as ApiErrorBody | undefined);
    if (shouldToast(response.status, skipErrorToast)) showErrorToast(getErrorMessage(apiError));
    throw apiError;
  }

  return payload as T;
}
